/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {Device} from '@ion/core/src/types/entities';
import {ServiceError, SmartSearch} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {FilterChips} from '@ion/dls/src/types/editors';

// input selectors ...
const selectGrid                 = ({devices: {grid}}) => grid;
const selectIncludeSubAccounts   = ({devices}) => devices.includeSubAccounts;
const selectDevicesSelected      = state => selectGrid(state).selectedDevices;
const selectAreTotalRowsSelected = state => selectGrid(state).areTotalRowsSelected;
const selectTotalRowsCount       = state => selectGrid(state).totalRowsCount;
const selectDevicesSearch        = state => selectGrid(state).search;
const selectDevicesGridSearch    = state => selectGrid(state).gridSearch;
const selectDevicesFilters       = state => selectGrid(state).filters;
const selectDeviceGridRefresh    = state => selectGrid(state).refresh;
const selectGridFilterModel      = state => selectGrid(state).filterModel;
const selectGridColumnState      = state => selectGrid(state).columnState;

const selectDevicesError = ({devices: {devices}}) => devices.error;

const selectAdd          = ({devices: {add}}) => add;
const selectAddIsEditing = state => selectAdd(state).isEditing;

const selectAudit                  = ({devices: {audit}}) => audit;
const selectAuditIsEditing         = state => selectAudit(state).isEditing;
const selectAuditSelected          = state => selectAudit(state).selected;
const selectAuditRecord            = state => selectAudit(state).auditRecord;
const selectAuditShowChangesDialog = state => selectAudit(state).showChangesDialog;

const selectDestroy          = ({devices: {destroy}}) => destroy;
const selectDestroyResult    = state => selectDestroy(state).result;
const selectDestroySelected  = state => selectDestroy(state).selected;
const selectDestroyIsEditing = state => selectDestroy(state).isEditing;
const selectDestroyLoading   = state => selectDestroy(state).loading;
const selectDestroyError     = state => selectDestroy(state).error;

const selectEdit            = ({devices: {edit}}) => edit;
const selectEditIsEditing   = state => selectEdit(state).isEditing;
const selectEditResult      = state => selectEdit(state).result;

const selectView            = ({devices: {view}}) => view;
const selectViewIsEditing   = state => selectView(state).isEditing;

const selectViewAlertConfig          = ({devices: {viewAlertConfig}}) => viewAlertConfig;
const selectViewAlertConfigIsEditing = state => selectViewAlertConfig(state).isEditing;
const selectViewAlertConfigSelected  = state => selectViewAlertConfig(state).selected;
const selectViewAlertConfigResult    = state => selectViewAlertConfig(state).result;
const selectViewAlertConfigLoading   = state => selectViewAlertConfig(state).loading;

const selectViewEvent          = ({devices: {viewEvent}}) => viewEvent;
const selectViewEventIsEditing = state => selectViewEvent(state).isEditing;
const selectViewEventSelected  = state => selectViewEvent(state).selected;

const selectBulkAdd          = ({devices: {bulkAdd}}) => bulkAdd;
const selectBulkAddIsEditing = state => selectBulkAdd(state).isEditing;

const selectBulkDelete          = ({devices: {bulkDelete}}) => bulkDelete;
const selectBulkDeleteResult    = state => selectBulkDelete(state).result;
const selectBulkDeleteSelected  = state => selectBulkDelete(state).selected;
const selectBulkDeleteIsEditing = state => selectBulkDelete(state).isEditing;
const selectBulkDeleteLoading   = state => selectBulkDelete(state).loading;
const selectBulkDeleteError     = state => selectBulkDelete(state).error;
const selectBulkDeleteRecord    = state => selectBulkDelete(state).record;

const selectBulkEdit          = ({devices: {bulkEdit}}) => bulkEdit;
const selectBulkEditIsEditing = state => selectBulkEdit(state).isEditing;

const selectBulkImport            = ({devices: {bulkImport}}) => bulkImport;
const selectBulkImportIsImporting = state => selectBulkImport(state).isImporting;

const selectDeviceMove            = ({devices: {move}}) => move;
const selectDeviceMoveSelected    = state => selectDeviceMove(state).selected;
const selectDeviceMoveShowDialog  = state => selectDeviceMove(state).isEditing;
const selectDeviceMoveResult      = state => selectDeviceMove(state).result;
const selectDeviceviewMovedErrors = state => selectDeviceMove(state).errors;
const selectDeviceMoveShowLoading = state => selectDeviceMove(state).loading;
const selectDeviceviewMovedResult = state => selectDeviceMove(state).viewMovedDevices;

const selectBulkSCISetting              = ({devices: {bulkSCIUpdate}}) => bulkSCIUpdate;
const selectBulkSCISettingUpload        = state => selectBulkSCISetting(state).bulkUpload;
const selectBulkSCISettingUpdate        = state => selectBulkSCISetting(state).bulkUpdate;
const selectBulkSCISettingSelected      = state => selectBulkSCISetting(state).selected;
const selectBulkSCISettingLoading       = state => selectBulkSCISetting(state).loading;
const selectBulkSCISettingDialogLoading = state => selectBulkSCISetting(state).dialogLoading;

const selectBulkViewDevices             = ({devices: {bulkViewDevices}}) => bulkViewDevices;
const selectBulkViewDevicesSelected     = state => selectBulkViewDevices(state).selected;
const selectBulkViewDevicesShowDialog   = state => selectBulkViewDevices(state).showViewDialog;
const selectBulkViewDevicesGridDialog   = state => selectBulkViewDevices(state).showGridDialog;
const selectBulkViewDevicesGridSearch   = state => selectBulkViewDevices(state).searchText;
const selectBulkViewDevicesTabs         = state => selectBulkViewDevices(state).tabs;
const selectBulkViewDevicesSelectedTab  = state => selectBulkViewDevices(state).selectedTab;

const selectSentinelAssociation = ({devices: {sentinelAssociation}}) => sentinelAssociation;
const selectSecondaryIdentifier = state => selectSentinelAssociation(state).secondaryIdentifier;
const selectSecondaryTab        = state => selectSentinelAssociation(state).showSecondaryTab;
const selectSentinelTab         = state => selectSentinelAssociation(state).showSentinelTab;

// output selectors ...
export const adminDevicesGridFilterModelSelector = createDeepEqualSelector(selectGridFilterModel,
  (filterModel: FilterModel): FilterModel => filterModel
);
export const adminDevicesGridColumnStateSelector = createDeepEqualSelector(selectGridColumnState,
  (columnState: ColumnState[]): ColumnState[] => columnState
);

export const adminDevicesAddIsEditingSelector = createSelector(selectAddIsEditing,
  (isEditing: boolean): boolean => isEditing
);

export const devicesSelectedSelector = createDeepEqualSelector(selectDevicesSelected,
  (selectedDevices: Device[]): Device[] => selectedDevices
);

export const devicesIncludeSubAccountsSelector = createSelector(selectIncludeSubAccounts,
  (includeSubAccounts: boolean): boolean => includeSubAccounts
);

export const deviceSecondaryDeviceSelector = createSelector(selectSecondaryIdentifier,
  (secondaryIdentifier: string): string => secondaryIdentifier
);

export const deviceSecondaryTabSelector = createSelector(selectSecondaryTab,
  (showSecondaryTab: boolean): boolean => showSecondaryTab
);

export const deviceSentinelTabSelector = createSelector(selectSentinelTab,
  (showSentinelTab: boolean): boolean => showSentinelTab
);

export const devicesAreTotalRowsSelectedSelector = createSelector(selectAreTotalRowsSelected,
  (areTotalRowsSelected: boolean): boolean => areTotalRowsSelected
);

export const devicesTotalRowsCountSelector = createSelector(selectTotalRowsCount,
  (totalRowsCount: number): number => totalRowsCount
);

export const devicesSearchSelector = createDeepEqualSelector(selectDevicesSearch,
  (search: SmartSearch): SmartSearch => search
);

export const devicesGridSearchSelector = createDeepEqualSelector(selectDevicesGridSearch,
  (search: SmartSearch): SmartSearch => search
);

export const devicesError = createDeepEqualSelector(selectDevicesError,
  (error: ServiceError): ServiceError => error
);

export const devicesFiltersSelector = createDeepEqualSelector(selectDevicesFilters,
  (filters: FilterChips): FilterChips => filters
);

export const devicesGridRefreshSelector = createSelector(selectDeviceGridRefresh,
  (shouldRefresh: boolean): boolean => shouldRefresh
);

export const adminDevicesAuditIsEditingSelector = createSelector(selectAuditIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesAuditSelectedSelector = createDeepEqualSelector(selectAuditSelected,
  (selected: Device): Device => selected
);
export const adminDevicesAuditRecordSelector = createDeepEqualSelector(selectAuditRecord,
  (auditRecord: AuditRecordType): AuditRecordType => auditRecord
);
export const adminDevicesAuditShowChangesDialogSelector = createSelector(selectAuditShowChangesDialog,
  (showChangesDialog: boolean): boolean => showChangesDialog
);

export const adminDevicesDestroyResultSelector = createDeepEqualSelector(selectDestroyResult,
  (result: Device): Device => result
);
export const adminDevicesDestroySelectedSelector = createDeepEqualSelector(selectDestroySelected,
  (selected: Device): Device => selected
);
export const adminDevicesDestroyIsEditingSelector = createSelector(selectDestroyIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesDestroyLoadingSelector = createSelector(selectDestroyLoading,
  (loading: boolean): boolean => loading
);
export const adminDevicesDestroyErrorSelector = createDeepEqualSelector(selectDestroyError,
  (error: ServiceError): ServiceError => error
);

export const adminDevicesEditIsEditingSelector = createSelector(selectEditIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesEditResultSelector = createSelector(selectEditResult,
  (result: Device): Device => result
);

export const adminDevicesViewIsEditingSelector = createSelector(selectViewIsEditing,
  (isEditing: boolean): boolean => isEditing
);

export const adminDevicesViewAlertConfigIsEditingSelector = createSelector(selectViewAlertConfigIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesViewAlertConfigSelectedSelector = createDeepEqualSelector(selectViewAlertConfigSelected,
  (selected: Device): Device => selected
);
export const adminDevicesViewAlertConfigResultSelector = createDeepEqualSelector(selectViewAlertConfigResult,
  (result: Device): Device => result
);
export const adminDevicesViewAlertConfigLoadingSelector = createSelector(selectViewAlertConfigLoading,
  (loading: boolean): boolean => loading
);

export const adminDevicesViewEventIsEditingSelector = createSelector(selectViewEventIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesViewEventSelectedSelector = createDeepEqualSelector(selectViewEventSelected,
  (selected: Device): Device => selected
);

export const adminDevicesBulkAddIsEditingSelector = createSelector(selectBulkAddIsEditing,
  (isEditing: boolean): boolean => isEditing
);

export const adminDevicesBulkDeleteResultSelector = createDeepEqualSelector(selectBulkDeleteResult,
  (result: Device[]): Device[] => result
);
export const adminDevicesBulkDeleteSelectedSelector = createDeepEqualSelector(selectBulkDeleteSelected,
  (selected: Device[]): Device[] => selected
);
export const adminDevicesBulkDeleteIsEditingSelector = createSelector(selectBulkDeleteIsEditing,
  (isEditing: boolean): boolean => isEditing
);
export const adminDevicesBulkDeleteLoadingSelector = createSelector(selectBulkDeleteLoading,
  (loading: boolean): boolean => loading
);
export const adminDevicesBulkDeleteErrorSelector = createDeepEqualSelector(selectBulkDeleteError,
  (error: ServiceError): ServiceError => error
);
export const adminDevicesBulkDeleteRecordSelector = createDeepEqualSelector(selectBulkDeleteRecord,
  (record: Device[]): Device[] => record
);

export const adminDevicesBulkEditIsEditingSelector = createSelector(selectBulkEditIsEditing,
  (isEditing: boolean): boolean => isEditing
);

export const adminDevicesBulkImportIsImportingSelector = createSelector(selectBulkImportIsImporting,
  (isImporting: boolean): boolean => isImporting
);

export const deviceMoveShowDialogSelector = createSelector(
  selectDeviceMoveShowDialog, (isEditing: boolean) => isEditing
);
export const deviceMoveSelectedDevicesSelector = createDeepEqualSelector(
  selectDeviceMoveSelected, selected => selected
);
export const deviceMoveShowLoadingSelector = createSelector(
  selectDeviceMoveShowLoading, (loading: boolean) => loading
);
export const deviceMoveResultSelector = createDeepEqualSelector(
  selectDeviceMoveResult, result => result
);
export const deviceMoveErrorsSelector = createDeepEqualSelector(
  selectDeviceviewMovedErrors, (errors: ServiceError[]): ServiceError[] => errors
);
export const adminDevicesViewMovedResultsSelector = createSelector(
  selectDeviceviewMovedResult, (viewMovedDevices: boolean) => viewMovedDevices
);

export const bulkSCISettingUploadDialogSelector = createSelector(
  selectBulkSCISettingUpload, (bulkUpload: boolean) => bulkUpload
);
export const bulkSCISettingUpdateDialogSelector = createSelector(
  selectBulkSCISettingUpdate, (bulkUpdate: boolean) => bulkUpdate
);
export const bulkSCISettingSelectedDevicesSelector = createDeepEqualSelector(
  selectBulkSCISettingSelected, selected => selected
);
export const bulkSCISettingLoadingDialogSelector = createSelector(
  selectBulkSCISettingLoading, (loading: boolean) => loading
);
export const bulkSCISettingDialogLoadingDialogSelector = createSelector(
  selectBulkSCISettingDialogLoading, (dialogLoading: boolean) => dialogLoading
);

export const viewDevicesSelectedSelector = createDeepEqualSelector(
  selectBulkViewDevicesSelected, selected => selected
);
export const viewDevicesShowDialogSelector = createSelector(
  selectBulkViewDevicesShowDialog, (showViewDialog: boolean) => showViewDialog
);
export const viewDevicesGridShowDialogSelector = createSelector(
  selectBulkViewDevicesGridDialog, (showGridDialog: boolean) => showGridDialog
);
export const viewDevicesGridSearchTextSelector = createSelector(
  selectBulkViewDevicesGridSearch, (searchText: string) => searchText
);
export const viewDevicesTabsSelector = createDeepEqualSelector(
  selectBulkViewDevicesTabs, tabs => tabs
);
export const viewDevicesSelectedTabSelector = createDeepEqualSelector(
  selectBulkViewDevicesSelectedTab, selectedTab => selectedTab
);
